/**
 * Normalize lease / tenant field inconsistencies across the app.
 *
 * Problem: Some paths write `leaseEnd` (tenantModel default), others write
 * `leaseEndDate` (manual tenant form). Status is sometimes `"active"` (lowercase)
 * and sometimes `"Active"` (titlecase). This module provides a single source of
 * truth so Dashboard, Communication, Units, etc. don't each re-implement the logic.
 */

/**
 * Parse a Firestore value that could be a Timestamp, string, or Date into a JS Date.
 * Returns null if the value is falsy or unparseable.
 */
export function toDate(value) {
  if (!value) return null;
  if (typeof value === "object" && typeof value.toDate === "function")
    return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Get the lease end date from a tenant record, checking both field names.
 * @param {Object} tenant
 * @returns {Date|null}
 */
export function getLeaseEndDate(tenant) {
  if (!tenant) return null;
  return (
    toDate(tenant.leaseEnd) ||
    toDate(tenant.leaseEndDate) ||
    toDate(tenant.endDate) ||
    null
  );
}

/**
 * Get the lease start date from a tenant record.
 */
export function getLeaseStartDate(tenant) {
  if (!tenant) return null;
  return (
    toDate(tenant.leaseStart) ||
    toDate(tenant.leaseStartDate) ||
    toDate(tenant.startDate) ||
    null
  );
}

/**
 * Is this tenant currently "active" regardless of casing?
 * Checks: status, leaseStatus, and the boolean active field.
 */
export function isTenantActive(tenant) {
  if (!tenant) return false;
  const s = (tenant.status || "").toLowerCase();
  const ls = (tenant.leaseStatus || "").toLowerCase();
  if (s === "active" || ls === "active") return true;
  if (tenant.active === true) return true;
  return false;
}

/**
 * Days until lease expires. Returns Infinity if no end date, negative if already expired.
 */
export function daysUntilLeaseEnd(tenant) {
  const end = getLeaseEndDate(tenant);
  if (!end) return Infinity;
  return (end - new Date()) / (1000 * 60 * 60 * 24);
}
